import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  AlertTitle,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { getWipTrackers } from '../../reducers/wip.reducer';

const WIPAnalytics = () => {
  const dispatch = useDispatch();

  // Get data from Redux
  const trackers = useSelector(state => state.wip.trackers);
  const loading = useSelector(state => state.wip.loading);

  // Load trackers on mount
  useEffect(() => {
    dispatch(getWipTrackers({}));
  }, [dispatch]);

  // Basic analytics from available tracker data
  const basicAnalytics = useMemo(() => {
    const totalTrackers = trackers.length;
    const seasons = [...new Set(trackers.map(t => t.season))].filter(Boolean);
    const vendors = [...new Set(trackers.map(t => t.vendor))].filter(Boolean);

    return {
      totalTrackers,
      uniqueSeasons: seasons.length,
      uniqueVendors: vendors.length,
      seasons,
      vendors,
    };
  }, [trackers]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          WIP Tracker Analytics
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Key performance insights across all work-in-progress items
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert
        severity="info"
        icon={<WarningAmberIcon />}
        sx={{ mb: 3, borderRadius: '8px' }}
      >
        <AlertTitle>Analytics Coming Soon</AlertTitle>
        This page will display comprehensive analytics once the backend provides
        detailed stage data for all WIP items. It will include:
      </Alert>

      {/* Basic Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#4B68D4' }}
              >
                {basicAnalytics.totalTrackers}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total WIP Trackers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#4CAF50' }}
              >
                {basicAnalytics.uniqueSeasons}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Seasons
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#FF9800' }}
              >
                {basicAnalytics.uniqueVendors}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Unique Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Planned Analytics Sections */}
      <Grid container spacing={3}>
        {/* Stage Delay Summary */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2} sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                📊 Average Delay by Stage
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Bar chart showing average delay in days for each production
                stage
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 280,
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Chart will display here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Delay Distribution */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2} sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                📈 Delay Distribution
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Pie chart categorizing items by delay severity
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 280,
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Pie chart will display here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendor Performance */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                🏭 Vendor Performance Scorecard
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Table ranking vendors by on-time delivery rate across key
                milestones
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Vendor performance table will display here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* At-Risk Items */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                ⚠️ At-Risk Items (Top 10)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Table showing items with critical delays (&gt;7 days) or overdue
                milestones
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  At-risk items table will display here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Production Stage Completion */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                ✅ Production Stage Completion Status
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Table showing completion progress across all production stages
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Stage completion table will display here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WIPAnalytics;
